import { SearchInstancesManagerService } from '@anecdotes/design-system/data-manipulation';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  ViewChild,
} from '@angular/core';
import { VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
import { fromEvent, firstValueFrom } from 'rxjs';
import { BaseRenderer } from '../base-renderer/base-renderer';
import { VirtualScrollContextService } from '../../services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export const VIRTUAL_SCROLL_BUFFER = 10;
export const SCROLL_OFFSET = -100;

/**
 * @deprecated Use app-virtual-scroll-renderer-v2 instead
 */
@Component({
  selector: 'app-virtual-scroll-renderer',
  templateUrl: './virtual-scroll-renderer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [VirtualScrollContextService],
})
export class VirtualScrollRendererComponent extends BaseRenderer {
  @ViewChild('scroller', { static: true })
  private virtualScroller: VirtualScrollerComponent;

  @HostBinding('class')
  private classes = 'block overflow overflow-auto';

  @Input() virtualScrollBuffer = VIRTUAL_SCROLL_BUFFER;
  @Input() scrollOffset: number;
  @Input() throttleTime = 0;

  get parentScrollElement(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  get hasScrollbar(): boolean {
    return this.virtualScroller?.viewPortInfo.maxScrollPosition > 0;
  }

  constructor(
    searchInstancesManagerService: SearchInstancesManagerService,
    elementRef: ElementRef<HTMLElement>,
    cd: ChangeDetectorRef,
    private virtualScrollContext: VirtualScrollContextService
  ) {
    super(searchInstancesManagerService, elementRef, cd);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.virtualScrollContext.init({
      throttleTimeMs: this.throttleTime,
    });

    fromEvent(this.parentScrollElement, 'scroll')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.virtualScrollContext.onScroll());
  }

  async scrollToId(itemId: string): Promise<void> {
    await firstValueFrom(this.isReady$);

    if (this.renderedIds.includes(itemId)) {
      await new Promise((resolve, _) =>
        this.virtualScroller.scrollInto(itemId, true, this.scrollOffset ?? SCROLL_OFFSET, undefined, () =>
          resolve(undefined)
        )
      );
    }
  }

  protected getItemsForRendering(): any[] {
    // it's because rendered items should preserve items ordering during rendering
    if (this.allItems && this.displayedItemIds) {
      return this.allItems.filter((item) => this.displayedItemIds.has(this.selectId(item)));
    }

    return [];
  }
}
